import type {
    ProductVariantAvailabilityWithChannel,
    Product as RawProduct,
    Image,
} from "@graphql/generated/types";
import type { Product, ProductChannel, ProductImage } from "./types";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ui/components/media-and-icons/image/imagePaths";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { activeSiteCountry } from "@config/site/site-config";

export const normalizeProductData = (product: RawProduct): Product => {
    const clonedProduct = removeTypename(structuredClone(product)) as RawProduct;

    const { masterData, key: sku } = clonedProduct;
    const { masterVariant } = masterData.current || {};
    const images = masterVariant?.images || [];

    if (!images?.length) {
        // If there are no images, add a placeholder image
        Logger.warn(ServiceType.NEXT_API, "ProductDetailPage missing product images", {
            tag: LogTag.MISSING_IMAGE,
            message: "No images available for product",
            sku,
            source: ServiceType.COMMERCE_TOOLS,
            country: activeSiteCountry?.toLowerCase() || "",
        });
    }

    const currentMasterData = clonedProduct.masterData.current;

    return {
        id: clonedProduct.id,
        sku: clonedProduct.key,
        name: currentMasterData.name || "",
        description: currentMasterData.description || "",
        attributes: currentMasterData.masterVariant.attributesRaw || [],
        images: mapImages(currentMasterData.masterVariant.images),
        price: {
            centAmount: currentMasterData.masterVariant?.price?.value?.centAmount ?? 0,
            fractionDigits: currentMasterData.masterVariant?.price?.value?.fractionDigits ?? 0,
        },
        variants: mapVariants(currentMasterData.variants),
    };
};

export function mapAvailableChannels(
    channels: ProductVariantAvailabilityWithChannel[]
): ProductChannel[] {
    return (
        channels?.map(({ channel, availability }) => ({
            id: channel?.id || "",
            key: channel?.key || "",
            isOnStock: availability?.isOnStock || false,
            availableQuantity: availability?.availableQuantity || 0,
        })) || []
    );
}

function mapVariants(variants: RawProduct["masterData"]["current"]["variants"]) {
    return (
        variants?.map((variant) => ({
            id: variant.id,
            sku: variant.sku,
            price: variant.price,
            attributesRaw: variant.attributesRaw,
            availability: mapAvailableChannels(variant?.availability?.channels?.results),
        })) || []
    );
}

function mapImages(images: Image[]): ProductImage[] {
    if (!images?.length) {
        return [
            {
                url: PRODUCT_IMAGE_PLACEHOLDER_URL,
                label: "placeholder",
                dimensions: { width: 1200, height: 1200 },
            },
        ];
    }

    return images.map((image) => ({
        url: image.url,
        label: image.label || undefined,
        dimensions: {
            height: image.dimensions?.height || 0,
            width: image.dimensions?.width || 0,
        },
    }));
}

function removeTypename(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(removeTypename);
    }

    if (obj !== null && typeof obj === "object") {
        const newObj: Record<string, any> = {};
        for (const key of Object.keys(obj)) {
            if (key !== "__typename") {
                newObj[key] = removeTypename(obj[key]);
            }
        }
        return newObj;
    }

    return obj;
}
